<script>
	// NPM Modules
	import dayjs from 'dayjs';
	// Components
	import FontIcon from '@/Components/FontIcon.svelte';
	import Prompt from '@/Components/Prompt.svelte';
	import Icon from '@/Components/Icon.svelte';
	// Partials
	import ManualResultSGM from '@/Partials/Sport/Markets/ManualResult/_ManualResultSGM.svelte';
	import SGMSelectionBets from '@/Partials/Sport/Markets/_SGMSelectionBets.svelte';
	import AdjustSelectionLiability from '@/Partials/Liabilities/_AdjustSelectionLiability.svelte';
	// Constants
	import { SportSelectionStatus } from '@/Constants/SportSelection.js';
	// Services
	import { CurrencyFormat } from '@/Services/Helpers.js';
	import { notify } from '@/Services/Notify.js';
	// Stores
	import SportcastSelectionStore from '@/Stores/Sport/SportcastSelectionStore.js';
	import CompetitionStore from '@/Stores/Sport/CompetitionStore';
	import ClassificationStore from '@/Stores/Racing/ClassificationStore';

	export let event;
	export let sportscastSelectionId;
	export let refreshEvent = undefined;

	const StatusColours = {
		[SportSelectionStatus.Active]: 'green-500',
		[SportSelectionStatus.Void]: 'orange-400',
		[SportSelectionStatus.Pending]: 'gray-500',
		[SportSelectionStatus.Resulted]: 'purple-500',
		[SportSelectionStatus.Win]: 'purple-500',
		[SportSelectionStatus.Lost]: 'purple-500',
		Unknown: 'gray-100'
	};

	let showManualResultSGM = false;
	let showPromptUnresultSelection = false;

	$: selection =
		sportscastSelectionId &&
		$SportcastSelectionStore &&
		$SportcastSelectionStore.firstWhere('id', sportscastSelectionId);
	$: classification = event && $CompetitionStore && $ClassificationStore && getEventClassification();

	$: currentLiabilityLimit = event && selection && getCurrentLiabilityLimit();
	$: currentMultiLiabilityLimit = event && selection && getCurrentMultiLiabilityLimit();
	$: isSelectionResulted =
		selection &&
		[
			SportSelectionStatus.Win,
			SportSelectionStatus.Lost,
			SportSelectionStatus.Void,
			SportSelectionStatus.Resulted
		].includes(selection.status.id);
	$: selectionCanBeUnresulted =
		selection && (selection.resulted_at != null ? dayjs().diff(dayjs(selection.resulted_at), 'hour') < 48 : false);

	let showBets = false;

	let showLiabilityAdjustment = false;

	function getEventClassification() {
		let competition = $CompetitionStore.firstWhere('id', event.competition.id);
		if (competition !== null) {
			return $ClassificationStore.firstWhere('id', competition.classification.id.toString());
		}
	}

	function getCurrentLiabilityLimit() {
		if (
			selection.liabilities &&
			selection.liabilities.settings &&
			selection.liabilities.settings.selection_limit !== null
		) {
			return CurrencyFormat(selection.liabilities.settings.selection_limit);
		}
		if (classification && classification.sgm_selection_liability_limit !== null) {
			return CurrencyFormat(classification.sgm_selection_liability_limit);
		}

		return 'None set';
	}

	function getCurrentMultiLiabilityLimit() {
		if (
			selection.liabilities &&
			selection.liabilities.settings &&
			selection.liabilities.settings.multi_selections_limit !== null
		) {
			return CurrencyFormat(selection.liabilities.settings.multi_selections_limit);
		}
		if (classification && classification.sgm_multi_selections_liability_limit !== null) {
			return CurrencyFormat(classification.sgm_multi_selections_liability_limit);
		}

		return 'None set';
	}

	function onManualResultClick() {
		showManualResultSGM = true;
	}

	function unresultSelection() {
		selection
			.unresult()
			.then(() => {
				notify({
					type: 'success',
					title: selection.name,
					message: 'Selection successfully unresulted',
					timeout: 3
				});
				if (refreshEvent) {
					refreshEvent();
				}
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Failed to unresult selection',
					timeout: 5
				});
			});
	}
</script>

{#if showManualResultSGM}
	<ManualResultSGM
		selection_id={selection.id}
		eventName={event.name}
		bind:open={showManualResultSGM}
		on:resulted={refreshEvent}
	/>
{/if}
{#if showLiabilityAdjustment}
	<AdjustSelectionLiability bind:open={showLiabilityAdjustment} {event} sportcast_selection={selection} />
{/if}
{#if showPromptUnresultSelection}
	<Prompt bind:open={showPromptUnresultSelection} title="Confirm unresulting selection" onConfirm={unresultSelection}>
		<div class="m-3">
			<span class="text-xl">
				This will un-result all the bets already placed and cancel withdrawals, only do this when you have received
				incorrect results
			</span>
			<br />
			<br />
			<span class="font-semibold text-xl">Are you sure you want to unfinalise the selection?</span>
		</div>
	</Prompt>
{/if}

{#if selection}
	<div class="w-full pb-2 pl-2 pr-2 pt-1 mb-4 border hover:bg-gray-100">
		<div class="flex flex-col flex-grow">
			<div class="flex flex-row gap-x-2">
				<div class="flex flex-col justify-between flex-grow">
					<div class="grid text-sm text-center w-full">
						<!-- HEADER -->
						<!-- ROW 1 -->
						<div
							class="col-start-1 text-left whitespace-nowrap border-b border-row-separator py-1 flex flex-grow justify-between"
						>
							<div class="flex space-x-1 items-center">
								<div
									title={`Status: ${selection?.status?.name ?? 'Unknown'}`}
									class="w-4 h-4 {`bg-${
										StatusColours[selection?.status?.id ?? 'Unknown']
									}`} rounded-full cursor-help flex flex-row justify-center items-center"
								>
									{#if !selection?.status?.id}
										<span class="text-red-500"> ? </span>
									{/if}
								</div>
								<div class="whitespace-pre-wrap">{selection.name}</div>
							</div>
							<div class="flex flex-shrink items-center space-x-2 w-40 justify-end">
								{#if [SportSelectionStatus.Active, SportSelectionStatus.Pending].includes(selection?.status?.id)}
									<button
										on:click={onManualResultClick}
										class="flex items-center justify-center bg-danger btn btn-primary-action w-full py-1.5 text-xs"
									>
										Manual Result
									</button>
								{/if}

								<button disabled class="btn bg-blue-500 text-white rounded-full cursor-default"> SGM </button>
								{#if isSelectionResulted}
									{#if selectionCanBeUnresulted}
										<button on:click={() => (showPromptUnresultSelection = true)} title="Unresult market">
											<Icon name="unfinalise" classes="block w-5 h-5 text-gray-800 inline" />
										</button>
									{:else}
										<span title="Can not unresult market">
											<Icon name="unfinalised-locked" classes="block w-5 h-5 text-gray-500 inline" />
										</span>
									{/if}
								{/if}
							</div>
						</div>
					</div>
				</div>
			</div>
			<div class="flex flex-row border-b">
				<table class="w-full text-left">
					<tr>
						<th class="px-2 py-0.5">Liability Limit</th>
						<th class="px-2 py-0.5">Multi Liability Limit</th>
						<th class="px-1.5 py-0.5">Single Result</th>
						<th class="px-1.5 py-0.5">Single Hold</th>
						<th class="px-1.5 py-0.5">Multi Result</th>
						<th class="px-1.5 py-0.5">Multi Hold</th>
						<th class="px-1.5 py-0.5">Bets</th>
					</tr>
					<tr>
						<td class="px-1.5 py-0.5">
							<button
								class="h-full w-full flex flex-row hover:border-blue-700 border-transparent border-2 hover:bg-blue-300 cursor-pointer"
								on:click={() => {
									showLiabilityAdjustment = true;
								}}
							>
								<span> {currentLiabilityLimit}</span>
							</button>
						</td>
						<td class="px-1.5 py-0.5">
							<button
								class="h-full w-full flex flex-row hover:border-blue-700 border-transparent border-2 hover:bg-blue-300 cursor-pointer"
								on:click={() => {
									showLiabilityAdjustment = true;
								}}
							>
								<span> {currentMultiLiabilityLimit}</span>
							</button>
						</td>
						<td class="px-1.5 py-0.5"
							>{selection.liabilities.result ? CurrencyFormat(selection.liabilities.result) : '-'}</td
						>
						<td class="px-1.5 py-0.5"
							>{selection.liabilities.hold ? CurrencyFormat(selection.liabilities.hold) : '-'}</td
						>
						<td class="px-1.5 py-0.5"
							>{selection.liabilities.multi_result ? CurrencyFormat(selection.liabilities.multi_result) : '-'}</td
						>
						<td class="px-1.5 py-0.5"
							>{selection.liabilities.multi_hold ? CurrencyFormat(selection.liabilities.multi_hold) : '-'}</td
						>
						<td class="px-1.5 py-0.5">{selection.bet_count == 0 ? '-' : selection.bet_count}</td>
					</tr>
				</table>
			</div>
			<div>
				<button
					on:click={() => (showBets = !showBets)}
					class="text-left w-full py-2 mt-1 text-sm bg-gray-200 hover:bg-gray-300 cursor-pointer"
				>
					<FontIcon icon="uniE90F" className="pl-2" />
					Bet list
				</button>
				{#if showBets && selection}
					<SGMSelectionBets {selection} {event} />
				{/if}
			</div>
		</div>
	</div>
{/if}
